import { Component, computed, signal, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { CheckboxModule } from 'primeng/checkbox';
import { TooltipModule } from 'primeng/tooltip';
import { HttpCancelacionSitioRepository } from 'src/app/infrastructure/adapters/cancelacion-sitio/http-cancelacion-sitio.repository';
import {
  CancelacionSitioEncabezado,
  CancelacionSitioDetalle
} from 'src/app/domain/model/cancelacion-sitio/cancelacion-sitio';
import { cancelacionSitioDetallePreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';

/**
 * Componente de Detalles de Cancelación en Sitio.
 * Migrado desde: DetalleCancelacionSitioViewImpl.java (GWT)
 * Muestra:
 * - Encabezado (solo lectura): nombre, oficina, fechas
 * - Grid de detalles: programaciones asociadas
 * - Botón "Agregar Detalle" (solo si estado = 'PEN')
 * - Botón "Regresar"
 *
 * @author Equipo Migración GIROP
 * @version 1.0
 * @since Mayo 2026
 */
@Component({
  selector: 'app-detalle-cancelacion-sitio',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    TableModule,
    ButtonModule,
    ToastModule,
    ProgressSpinnerModule,
    ConfirmDialogModule,
    DialogModule,
    CheckboxModule,
    TooltipModule
  ],
  providers: [MessageService, ConfirmationService],
  styleUrls: ['../../shared/styles/grid-table.styles.scss'],
  template: `
    <!-- Overlay de procesando -->
    @if (cargando()) {
      <div
        class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      >
        <div
          class="bg-white rounded-lg p-8 flex flex-col items-center gap-4 shadow-xl"
        >
          <p-progressSpinner strokeWidth="4" />
          <span class="text-gray-700 font-medium">{{ mensajeCarga() }}</span>
        </div>
      </div>
    }

    <div
      class="p-3 sm:p-4 lg:p-6 max-w-7xl mx-auto flex flex-col gap-4 overflow-x-hidden"
    >
      <!-- Header con botón regresar -->
      <div
        class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6"
      >
        <div>
          <span class="text-sm text-gray-600 mb-2 flex items-center gap-2"
            >Cancelaciones en Sitio >><b>
              Detalles de Cancelación en Sitio</b
            ></span
          >
          <h1 class="text-2xl font-bold text-gray-800">
            Detalles de Cancelación en Sitio
          </h1>
          <p class="text-sm text-gray-600">
            Programaciones asociadas al encabezado de cancelación
          </p>
        </div>
        <div class="flex justify-end">
          <button
            pButton
            type="button"
            severity="secondary"
            class="p-button-outlined"
            icon="pi pi-arrow-left"
            label="Regresar"
            (click)="regresar()"
            pTooltip="Regresar al listado de Cancelaciones en Sitio"
          ></button>
        </div>
      </div>

      <!-- Card del Encabezado (solo lectura) -->
      @if (encabezado()) {
        <p-card header="Datos del Encabezado" class="mb-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="flex flex-col gap-1">
              <label class="font-medium text-gray-700">Nombre</label>
              <span class="px-3 py-2 rounded border border-gray-300">{{
                encabezado()!.dsencabezado
              }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <label class="font-medium text-gray-700">Oficina</label>
              <span class="px-3 py-2 rounded border border-gray-300">{{
                encabezado()!.dsoficina
              }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <label class="font-medium text-gray-700">Fecha Inicial</label>
              <span class="px-3 py-2 rounded border border-gray-300">{{
                formatearFecha(encabezado()!.feiniCancelacion)
              }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <label class="font-medium text-gray-700">Fecha Final</label>
              <span class="px-3 py-2 rounded border border-gray-300">{{
                formatearFecha(encabezado()!.fefinCancelacion)
              }}</span>
            </div>
          </div>
          <div class="mt-4 flex items-center justify-between">
            <div class="flex items-center gap-4">
              <span class="text-sm">
                <span class="font-medium">Estado:</span>
                <span
                  [class]="getEstadoClass(encabezado()!.cdestado)"
                  class="ml-2 px-2 py-1 rounded text-sm"
                >
                  {{ encabezado()!.dsestado || encabezado()!.cdestado }}
                </span>
              </span>
              <span class="text-sm text-gray-600">
                <span class="font-medium">Total Detalles:</span>
                {{ detalles().length }}
              </span>
            </div>
            @if (puedeEnviarANomina()) {
              <button
                pButton
                type="button"
                severity="success"
                class="p-button-outlined"
                icon="pi pi-send"
                label="Enviar a Nómina"
                (click)="enviarANomina()"
              ></button>
            }
          </div>
        </p-card>
      }

      <!-- Tabla de Detalles -->
      <p-card header="Programaciones Asociadas" class="mb-6">
        <ng-template pTemplate="header">
          <div class="flex justify-between items-center">
            <span class="text-xl font-semibold"></span>
            @if (puedeAgregarDetalles()) {
              <button
                pButton
                type="button"
                severity="info"
                class="p-button-outlined"
                icon="pi pi-plus"
                label="Agregar Detalle"
                (click)="abrirDialogAgregar()"
              ></button>
            }
          </div>
        </ng-template>

        @if (detalles().length === 0) {
          <div class="text-center py-12 text-gray-500">
            <p>No hay programaciones asociadas a este encabezado</p>
            @if (puedeAgregarDetalles()) {
              <p class="text-sm mt-2">
                Haga clic en "Agregar Detalle" para asociar programaciones
              </p>
            }
          </div>
        } @else {
          <div class="md:hidden space-y-3 p-3">
            @for (
              detalle of detalles();
              track detalle.nmconsecutivo ?? detalle.nmconsecProgramacion
            ) {
              <div
                class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
              >
                <div class="mb-3 min-w-0">
                  <div class="text-xs text-gray-500 dark:text-gray-400">
                    Programación
                  </div>
                  <div class="font-semibold text-gray-800 dark:text-gray-100">
                    {{ detalle.nmconsecProgramacion }}
                  </div>
                </div>
                <div class="grid grid-cols-1 gap-2 text-sm mb-3">
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Consultor:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      detalle.asesor
                    }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Tipo cita:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      detalle.tipoCita
                    }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Municipio origen:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      detalle.municipioOrigen || '-'
                    }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Municipio entrega:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      detalle.municipioEntrega || '-'
                    }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Población:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      detalle.poblacion || '-'
                    }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Fecha prog.:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      formatearFecha(detalle.fechaProgramacion)
                    }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Valor transp.:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      formatearMoneda(detalle.valorTransporte)
                    }}</span>
                  </div>
                </div>
                @if (puedeAgregarDetalles()) {
                  <button
                    pButton
                    icon="pi pi-trash"
                    label="Eliminar"
                    class="p-button-danger p-button-outlined p-button-sm min-h-[44px] w-full"
                    (click)="eliminarDetalle(detalle)"
                  ></button>
                }
              </div>
            }
          </div>

          <div class="hidden md:block">
            <div
              role="region"
              [attr.aria-label]="detalleTableConfig().a11y.tableAriaLabel"
              class="sr-only"
            ></div>
            <p-table
              [resizableColumns]="true"
              columnResizeMode="expand"
              [value]="detalles()"
              [styleClass]="detalleTableStyleClass()"
              [scrollable]="true"
              [responsiveLayout]="'scroll'"
              [tableStyle]="{
                'min-width': detalleTableConfig().responsive.minWidth || '72rem'
              }"
              [scrollHeight]="
                detalleTableConfig().responsive.scrollHeight || '420px'
              "
            >
              <ng-template pTemplate="header">
                <tr>
                  <th pResizableColumn style="width: 100px">
                    <span class="grid-header-text" title="Programación"
                      >Programación</span
                    >
                  </th>
                  <th pResizableColumn>
                    <span class="grid-header-text" title="Consultor"
                      >Consultor</span
                    >
                  </th>
                  <th pResizableColumn>
                    <span class="grid-header-text" title="Tipo Cita"
                      >Tipo Cita</span
                    >
                  </th>
                  <th pResizableColumn>
                    <span class="grid-header-text" title="Municipio Origen"
                      >Municipio Origen</span
                    >
                  </th>
                  <th pResizableColumn>
                    <span class="grid-header-text" title="Municipio Entrega"
                      >Municipio Entrega</span
                    >
                  </th>
                  <th pResizableColumn>
                    <span class="grid-header-text" title="Población"
                      >Población</span
                    >
                  </th>
                  <th pResizableColumn style="width: 120px">
                    <span class="grid-header-text" title="Fecha Prog."
                      >Fecha Prog.</span
                    >
                  </th>
                  <th pResizableColumn style="width: 120px" class="text-right">
                    <span class="grid-header-text" title="Valor Transp."
                      >Valor Transp.</span
                    >
                  </th>
                  @if (puedeAgregarDetalles()) {
                    <th
                      pResizableColumn
                      style="width: 80px"
                      class="text-center"
                    >
                      <span class="grid-header-text" title="Acciones"
                        >Acciones</span
                      >
                    </th>
                  }
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-detalle>
                <tr>
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="textoTabla(detalle.nmconsecProgramacion)"
                      >{{ textoTabla(detalle.nmconsecProgramacion) }}</span
                    >
                  </td>
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="textoTabla(detalle.asesor)"
                      >{{ textoTabla(detalle.asesor) }}</span
                    >
                  </td>
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="textoTabla(detalle.tipoCita)"
                      >{{ textoTabla(detalle.tipoCita) }}</span
                    >
                  </td>
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="textoTabla(detalle.municipioOrigen)"
                      >{{ textoTabla(detalle.municipioOrigen) }}</span
                    >
                  </td>
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="textoTabla(detalle.municipioEntrega)"
                      >{{ textoTabla(detalle.municipioEntrega) }}</span
                    >
                  </td>
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="textoTabla(detalle.poblacion)"
                      >{{ textoTabla(detalle.poblacion) }}</span
                    >
                  </td>
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="
                        textoTabla(formatearFecha(detalle.fechaProgramacion))
                      "
                      >{{
                        textoTabla(formatearFecha(detalle.fechaProgramacion))
                      }}</span
                    >
                  </td>
                  <td class="text-right">
                    <span
                      class="grid-cell-text"
                      [attr.title]="
                        textoTabla(formatearMoneda(detalle.valorTransporte))
                      "
                      >{{
                        textoTabla(formatearMoneda(detalle.valorTransporte))
                      }}</span
                    >
                  </td>
                  @if (puedeAgregarDetalles()) {
                    <td class="text-center">
                      <button
                        pButton
                        icon="pi pi-trash"
                        class="p-button-rounded p-button-text p-button-sm p-button-danger min-w-[44px] min-h-[44px]"
                        pTooltip="Eliminar detalle"
                        (click)="eliminarDetalle(detalle)"
                      ></button>
                    </td>
                  }
                </tr>
              </ng-template>
              <ng-template pTemplate="emptymessage">
                <tr>
                  <td
                    [attr.colspan]="puedeAgregarDetalles() ? 9 : 8"
                    class="text-center py-8 text-gray-500"
                  >
                    No hay programaciones asociadas
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </div>
        }
      </p-card>
    </div>

    <!-- Dialog para Agregar Detalles -->
    <p-dialog
      header="Agregar Programaciones"
      [(visible)]="showDialogAgregar"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '60rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '88vw', '768px': '92vw', '640px': '96vw' }"
      [closable]="true"
      [draggable]="false"
    >
      @if (cargandoDisponibles()) {
        <div class="flex justify-center items-center py-12">
          <p-progressSpinner strokeWidth="4" />
        </div>
      } @else if (programacionesDisponibles().length === 0) {
        <div class="text-center py-12 text-gray-500">
          <p>No hay programaciones disponibles para agregar</p>
          <p class="text-sm mt-2">
            Las programaciones deben estar en el rango de fechas del encabezado
            y no estar asociadas a otro encabezado
          </p>
        </div>
      } @else {
        <p class="mb-4 text-gray-600">
          Seleccione las programaciones que desea agregar al encabezado ({{
            seleccionadasArray.length
          }}
          seleccionadas)
        </p>
        <div class="md:hidden space-y-3 p-3">
          @for (
            prog of programacionesDisponibles();
            track prog.nmconsecProgramacion
          ) {
            <div
              class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
            >
              <div class="mb-3 flex items-start justify-between gap-2">
                <div class="min-w-0">
                  <div class="text-xs text-gray-500 dark:text-gray-400">
                    Consultor
                  </div>
                  <div
                    class="font-semibold text-gray-800 dark:text-gray-100 truncate"
                  >
                    {{ prog.asesor }}
                  </div>
                </div>
                <p-checkbox
                  [binary]="true"
                  [ngModel]="isProgramacionSeleccionada(prog)"
                  (ngModelChange)="toggleProgramacionSeleccion(prog)"
                >
                </p-checkbox>
              </div>
              <div class="grid grid-cols-1 gap-2 text-sm">
                <div>
                  <span class="text-gray-500 dark:text-gray-400"
                    >Tipo cita:</span
                  >
                  <span class="text-gray-800 dark:text-gray-200">{{
                    prog.tipoCita
                  }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400"
                    >Municipio origen:</span
                  >
                  <span class="text-gray-800 dark:text-gray-200">{{
                    prog.municipioOrigen || '-'
                  }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400"
                    >Municipio entrega:</span
                  >
                  <span class="text-gray-800 dark:text-gray-200">{{
                    prog.municipioEntrega || '-'
                  }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400"
                    >Fecha prog.:</span
                  >
                  <span class="text-gray-800 dark:text-gray-200">{{
                    formatearFecha(prog.fechaProgramacion)
                  }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400"
                    >Valor transp.:</span
                  >
                  <span class="text-gray-800 dark:text-gray-200">{{
                    formatearMoneda(prog.valorTransporte)
                  }}</span>
                </div>
              </div>
            </div>
          }
        </div>

        <div class="hidden md:block">
          <div
            role="region"
            [attr.aria-label]="agregarTableConfig().a11y.tableAriaLabel"
            class="sr-only"
          ></div>
          <p-table
            [resizableColumns]="true"
            columnResizeMode="expand"
            [value]="programacionesDisponibles()"
            [(selection)]="seleccionadasArray"
            [dataKey]="
              (
                agregarTableConfig().selection.dataKey || 'nmconsecProgramacion'
              ).toString()
            "
            [selectionMode]="'multiple'"
            [styleClass]="agregarTableStyleClass()"
            [scrollable]="true"
            [responsiveLayout]="'scroll'"
            [tableStyle]="{
              'min-width': agregarTableConfig().responsive.minWidth || '72rem'
            }"
            [scrollHeight]="
              agregarTableConfig().responsive.scrollHeight || '400px'
            "
          >
            <ng-template pTemplate="header">
              <tr>
                <th pResizableColumn style="width: 50px">
                  <p-tableHeaderCheckbox></p-tableHeaderCheckbox>
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Consultor"
                    >Consultor</span
                  >
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Tipo Cita"
                    >Tipo Cita</span
                  >
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Municipio Origen"
                    >Municipio Origen</span
                  >
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Municipio Entrega"
                    >Municipio Entrega</span
                  >
                </th>
                <th pResizableColumn style="width: 120px">
                  <span class="grid-header-text" title="Fecha Prog."
                    >Fecha Prog.</span
                  >
                </th>
                <th pResizableColumn style="width: 120px" class="text-right">
                  <span class="grid-header-text" title="Valor Transp."
                    >Valor Transp.</span
                  >
                </th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-prog>
              <tr>
                <td>
                  <p-tableCheckbox [value]="prog"></p-tableCheckbox>
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="textoTabla(prog.asesor)"
                    >{{ textoTabla(prog.asesor) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="textoTabla(prog.tipoCita)"
                    >{{ textoTabla(prog.tipoCita) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="textoTabla(prog.municipioOrigen)"
                    >{{ textoTabla(prog.municipioOrigen) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="textoTabla(prog.municipioEntrega)"
                    >{{ textoTabla(prog.municipioEntrega) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="
                      textoTabla(formatearFecha(prog.fechaProgramacion))
                    "
                    >{{
                      textoTabla(formatearFecha(prog.fechaProgramacion))
                    }}</span
                  >
                </td>
                <td class="text-right">
                  <span
                    class="grid-cell-text"
                    [attr.title]="
                      textoTabla(formatearMoneda(prog.valorTransporte))
                    "
                    >{{
                      textoTabla(formatearMoneda(prog.valorTransporte))
                    }}</span
                  >
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>
      }

      <ng-template pTemplate="footer">
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
        >
          <button
            pButton
            type="button"
            icon="pi pi-times"
            severity="secondary"
            class="p-button-outlined min-h-[44px]"
            label="Cancelar"
            (click)="cerrarDialogAgregar()"
          ></button>
          <button
            pButton
            type="button"
            severity="success"
            class="p-button-success min-h-[44px]"
            label="Agregar Seleccionados"
            icon="pi pi-plus"
            [disabled]="seleccionadasArray.length === 0 || agregando()"
            [loading]="agregando()"
            (click)="agregarDetalles()"
          ></button>
        </div>
      </ng-template>
    </p-dialog>

    <p-toast></p-toast>
    <p-confirmDialog></p-confirmDialog>
  `
})
export class DetalleCancelacionSitioComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly repository = inject(HttpCancelacionSitioRepository);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);

  // State
  readonly cargando = signal(false);
  readonly mensajeCarga = signal('Cargando...');
  readonly encabezado = signal<CancelacionSitioEncabezado | null>(null);
  readonly detalles = signal<CancelacionSitioDetalle[]>([]);

  // Dialog agregar
  showDialogAgregar = false;
  readonly cargandoDisponibles = signal(false);
  readonly programacionesDisponibles = signal<CancelacionSitioDetalle[]>([]);
  seleccionadasArray: CancelacionSitioDetalle[] = [];
  readonly agregando = signal(false);

  readonly detalleTableConfig = computed(() =>
    cancelacionSitioDetallePreset.createConfig({
      data: this.detalles(),
      style: {
        tableClassName: 'tabla-cambio-orientacion',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      state: {
        loading: this.cargando(),
        emptyMessage: 'No hay programaciones asociadas'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: '72rem',
        scrollHeight: '420px',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel:
          'Tabla de programaciones asociadas a cancelacion en sitio',
        announceSelection: false,
        announcePageChanges: false,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly agregarTableConfig = computed(() =>
    cancelacionSitioDetallePreset.createConfig({
      data: this.programacionesDisponibles(),
      selection: {
        mode: 'multiple',
        dataKey: 'nmconsecProgramacion',
        selected: this.seleccionadasArray,
        onChange: (selected) => {
          this.seleccionadasArray =
            (selected as unknown as CancelacionSitioDetalle[]) || [];
        }
      },
      style: {
        tableClassName: 'tabla-cambio-orientacion',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      state: {
        loading: this.cargandoDisponibles(),
        emptyMessage: 'No hay programaciones disponibles para agregar'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: '72rem',
        scrollHeight: '400px',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de programaciones disponibles para agregar',
        announceSelection: true,
        announcePageChanges: false,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly detalleTableStyleClass = computed(() =>
    this.buildTableStyleClass(this.detalleTableConfig().style)
  );
  readonly agregarTableStyleClass = computed(() =>
    this.buildTableStyleClass(this.agregarTableConfig().style)
  );

  puedeAgregarDetalles(): boolean {
    const enc = this.encabezado();
    return enc !== null && enc.cdestado === 'PEN';
  }

  puedeEnviarANomina(): boolean {
    const enc = this.encabezado();
    return (
      enc !== null &&
      enc.cdestado === 'PEN' &&
      !enc.feenvioNomina &&
      this.detalles().length > 0
    );
  }

  isProgramacionSeleccionada(prog: CancelacionSitioDetalle): boolean {
    return this.seleccionadasArray.some(
      (s) => s.nmconsecProgramacion === prog.nmconsecProgramacion
    );
  }

  toggleProgramacionSeleccion(prog: CancelacionSitioDetalle): void {
    if (this.isProgramacionSeleccionada(prog)) {
      this.seleccionadasArray = this.seleccionadasArray.filter(
        (s) => s.nmconsecProgramacion !== prog.nmconsecProgramacion
      );
      return;
    }
    this.seleccionadasArray = [...this.seleccionadasArray, prog];
  }

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (id) {
      this.cargarDatos(id);
    } else {
      this.router.navigate(['/cancelaciones-sitio']);
    }
  }

  async cargarDatos(nmconsecutivo: number): Promise<void> {
    this.cargando.set(true);
    this.mensajeCarga.set('Cargando encabezado y detalles...');

    try {
      // Cargar encabezado y detalles en paralelo
      const [encabezado, detalles] = await Promise.all([
        this.repository.obtenerPorId(nmconsecutivo),
        this.repository.listarDetalles(nmconsecutivo)
      ]);

      this.encabezado.set(encabezado);
      this.detalles.set(detalles);
    } catch (error) {
      console.error('Error cargando datos:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudieron cargar los datos del encabezado'
      });
      this.router.navigate(['/cancelaciones-sitio']);
    } finally {
      this.cargando.set(false);
    }
  }

  async abrirDialogAgregar(): Promise<void> {
    this.showDialogAgregar = true;
    this.cargandoDisponibles.set(true);
    this.seleccionadasArray = [];
    this.programacionesDisponibles.set([]);

    try {
      const disponibles = await this.repository.listarProgramacionesDisponibles(
        this.encabezado()!.nmconsecutivo
      );
      this.programacionesDisponibles.set(disponibles);
    } catch (error) {
      console.error('Error cargando programaciones disponibles:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail:
          error instanceof Error
            ? error.message
            : 'No se pudieron cargar las programaciones disponibles'
      });
    } finally {
      this.cargandoDisponibles.set(false);
    }
  }

  cerrarDialogAgregar(): void {
    this.showDialogAgregar = false;
    this.seleccionadasArray = [];
    this.programacionesDisponibles.set([]);
  }

  async agregarDetalles(): Promise<void> {
    if (this.seleccionadasArray.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: 'Debe seleccionar al menos una programación'
      });
      return;
    }

    this.agregando.set(true);

    try {
      const nmconsecProgramaciones = this.seleccionadasArray.map(
        (p) => p.nmconsecProgramacion
      );

      const response = await this.repository.agregarDetalles(
        this.encabezado()!.nmconsecutivo,
        nmconsecProgramaciones
      );

      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: `Se agregaron ${response.cantidad} programaciones al encabezado`
      });

      this.cerrarDialogAgregar();

      // Recargar detalles
      const detalles = await this.repository.listarDetalles(
        this.encabezado()!.nmconsecutivo
      );
      this.detalles.set(detalles);
    } catch (error) {
      console.error('Error agregando detalles:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail:
          error instanceof Error
            ? error.message
            : 'No se pudieron agregar los detalles'
      });
    } finally {
      this.agregando.set(false);
    }
  }

  regresar(): void {
    this.router.navigate(['/cancelaciones-sitio']);
  }

  enviarANomina(): void {
    const enc = this.encabezado();
    if (!enc) return;

    this.confirmationService.confirm({
      message: `¿Está seguro de enviar a nómina el encabezado "${enc.dsencabezado}"?`,
      header: 'Confirmar Envío a Nómina',
      icon: 'pi pi-send',
      acceptLabel: 'Sí, Enviar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-outlined p-button-success p-button-sm',
      rejectButtonStyleClass:
        'p-button-outlined p-button-secondary p-button-sm',
      accept: async () => {
        this.cargando.set(true);
        this.mensajeCarga.set('Enviando a nómina...');

        try {
          const response = await this.repository.enviarANomina(
            enc.nmconsecutivo
          );
          if (response.exitoso) {
            this.messageService.add({
              severity: 'success',
              summary: 'Enviado',
              detail: response.mensaje
            });
            // Recargar datos para actualizar estado
            await this.cargarDatos(enc.nmconsecutivo);
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: response.mensaje
            });
          }
        } catch (error) {
          console.error('Error enviando a nómina:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail:
              error instanceof Error
                ? error.message
                : 'No se pudo enviar a nómina'
          });
        } finally {
          this.cargando.set(false);
        }
      }
    });
  }

  eliminarDetalle(detalle: CancelacionSitioDetalle): void {
    const enc = this.encabezado();
    if (!enc || !detalle.nmconsecutivo) return;

    this.confirmationService.confirm({
      message: `¿Está seguro de eliminar la programación ${detalle.nmconsecProgramacion} del encabezado?`,
      header: 'Confirmar Eliminación',
      icon: 'pi pi-trash',
      acceptLabel: 'Sí, Eliminar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-outlined p-button-danger p-button-sm',
      rejectButtonStyleClass:
        'p-button-outlined p-button-secondary p-button-sm',
      accept: async () => {
        this.cargando.set(true);
        this.mensajeCarga.set('Eliminando detalle...');

        try {
          await this.repository.eliminarDetalle(
            enc.nmconsecutivo,
            detalle.nmconsecutivo!
          );
          this.messageService.add({
            severity: 'success',
            summary: 'Eliminado',
            detail: 'Detalle eliminado correctamente'
          });
          // Recargar detalles
          const detalles = await this.repository.listarDetalles(
            enc.nmconsecutivo
          );
          this.detalles.set(detalles);
        } catch (error) {
          console.error('Error eliminando detalle:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail:
              error instanceof Error
                ? error.message
                : 'No se pudo eliminar el detalle'
          });
        } finally {
          this.cargando.set(false);
        }
      }
    });
  }

  // Utilidades de formato
  formatearFecha(fecha: string | undefined): string {
    if (!fecha) return '-';
    try {
      const date = new Date(fecha);
      return date.toLocaleDateString('es-CO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return fecha;
    }
  }

  formatearMoneda(valor: number | undefined): string {
    if (valor === undefined || valor === null) return '-';
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(valor);
  }

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  private buildTableStyleClass(style: {
    tableClassName: string;
    striped?: boolean;
    gridlines?: boolean;
  }): string {
    const classes = ['p-datatable-sm', style.tableClassName];
    if (style.gridlines !== false) {
      classes.push('p-datatable-gridlines');
    }
    if (style.striped !== false) {
      classes.push('p-datatable-striped');
    }
    return classes.join(' ');
  }

  getEstadoClass(estado: string): string {
    switch (estado?.toUpperCase()) {
      case 'PEN':
        return 'bg-yellow-100 text-yellow-800';
      case 'ENV':
        return 'bg-blue-100 text-blue-800';
      case 'PRO':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }
}
